import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { IResponse } from 'src/app/core/interfaces/response.interface';
import { ILutBenTypeModel } from 'src/app/core/models/lut-models/lut_ben_type.model';
import { ILutBldTypeModel } from 'src/app/core/models/lut-models/lut_bld_type.model';
import { ILutDocTypeModel } from 'src/app/core/models/lut-models/lut_doc_type.model';
import { ILutEnergyClassModel } from 'src/app/core/models/lut-models/lut_energy_class.model';
import { ILutPropEidosModel } from 'src/app/core/models/lut-models/lut_prop_eidos.model';
import { ILutUseDurationUnitsModel } from 'src/app/core/models/lut-models/lut_use_duration_units.model';
import { ILutEnotitaModel } from 'src/app/core/models/lut-models/lut_enotita.model';
import { ILutEstateTypeModel } from 'src/app/core/models/lut-models/lut_estate_type.model';
import { ILutIssuerHqModel } from 'src/app/core/models/lut-models/lut_issuer_hq.model';
import { ILutIssuerTypeModel } from 'src/app/core/models/lut-models/lut_issuer_type.model';
import { ILutKoinotitaModel } from 'src/app/core/models/lut-models/lut_koinotita.model';
import { ILutLandUseModel } from 'src/app/core/models/lut-models/lut_land_use.model';
import { ILutLandUseGpsModel } from 'src/app/core/models/lut-models/lut_land_use_gps.model';
import { ILutCatKxKfModel } from 'src/app/core/models/lut-models/lut_cat_kx_kf.model';
import { ILutOtaModel } from 'src/app/core/models/lut-models/lut_ota.model';
import { ILutPropertyTypeModel } from 'src/app/core/models/lut-models/lut_property_type.model';
import { ILutRightStatusModel } from 'src/app/core/models/lut-models/lut_right_status.model';
import { ILutRightTypeModel } from 'src/app/core/models/lut-models/lut_right_type.model';
import { ILutTopoTypeModel } from 'src/app/core/models/lut-models/lut_topo_type.model';
import { ILutWayownModel } from 'src/app/core/models/lut-models/lut_wayown.model';
import { IPropsModel } from 'src/app/core/models/props.model';

import { ErrorService } from 'src/app/services/error.service';
import { ToastService } from 'src/app/services/toast.service';
import { environment } from 'src/environments/environment';
import * as LutTablesActions from '../actions/lut-tables.actions';
import * as DocActions from '../actions/doc.actions';
import { AppState } from '../app.reducer';
import { ILutUtilityTypeModel } from 'src/app/core/models/lut-models/lut_utility_type.model';

import * as BuildingActions from '../actions/building.actions';
import { ILutMonthlyCostTypeModel } from 'src/app/core/models/lut-models/lut_monthly_cost_type.model';
@Injectable()
export class LutTablesEffects {
  constructor(
    public actions$: Actions,
    public http: HttpClient,
    private errorService: ErrorService,
    private store: Store<AppState>
  ) { }

  getLutTables$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getLutTables),
      switchMap(() => {
        return this.http.get<any>(environment.apiUrl + '/lutTables').pipe(
          map((x) => {
            return x.data;
          }),
          switchMap((responseData) => {
            return of(
              LutTablesActions.getLutTablesSuccess({
                payload: {
                  lutTables: responseData,
                },
              })
            );
          }),
          catchError((error) => {
            return of(
              LutTablesActions.lutTablesFail({
                payload: { error: this.errorService.mapError(error) },
              })
            );
          })
        );
      })
    )
  );

  getLandUseLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getLandUseLut),
      switchMap(({ payload }) => {
        return this.http
          .get<IResponse<ILutLandUseModel[]>>(
            environment.apiUrl + '/lutTables/landUseLut/selector',
            {
              params: {
                searchText: payload.searchText,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getLandUseLutSuccess({
                  payload: { landUseLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getLandUseGpsLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getLandUseGpsLut),
      switchMap(({ payload }) => {
        return this.http
          .get<IResponse<ILutLandUseGpsModel[]>>(
            environment.apiUrl + '/lutTables/landUseGpsLut/selector',
            {
              params: {
                searchText: payload.searchText,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getLandUseGpsLutSuccess({
                  payload: { landUseGpsLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getCatKxKfLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getCatKxKfLut),
      switchMap(({ payload }) => {
        return this.http
          .get<IResponse<ILutCatKxKfModel[]>>(
            environment.apiUrl + '/lutTables/catKxKfLut/selector',
            {
              params: {
                searchText: payload.searchText,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getCatKxKfLutSuccess({
                  payload: { catKxKfLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getBldTypeLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getBldTypeLut),
      switchMap(({ payload }) => {
        return this.http
          .get<IResponse<ILutBldTypeModel[]>>(
            environment.apiUrl + '/lutTables/bldTypeLut/selector',
            {
              params: {
                searchText: payload.searchText,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getBldTypeLutSuccess({
                  payload: { bldTypeLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getOtaLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getOtaLut),
      switchMap(({ payload }) => {
        return this.http
          .get<IResponse<ILutOtaModel[]>>(
            environment.apiUrl + '/lutTables/otaLut/selector',
            {
              params: {
                searchText: payload.searchText,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getOtaLutSuccess({
                  payload: { otaLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getEnotitesLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getEnotitesLut),
      switchMap(({ payload }) => {
        let query = {};
        query = { ...payload };
        return this.http
          .get<IResponse<ILutEnotitaModel[]>>(
            environment.apiUrl + '/lutTables/enotitesLut/selector',
            {
              params: {
                ...query,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getEnotitesLutSuccess({
                  payload: { enotitesLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getKoinotitesLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getKoinotitesLut),
      switchMap(({ payload }) => {
        let query = {};
        query = { ...payload };
        return this.http
          .get<IResponse<ILutKoinotitaModel[]>>(
            environment.apiUrl + '/lutTables/koinotitesLut/selector',
            {
              params: {
                ...query,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getKoinotitesLutSuccess({
                  payload: { koinotitesLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getPropertyTypeLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getPropTypeLut),
      switchMap(({ payload }) => {
        let query = {};
        query = { ...payload };
        return this.http
          .get<IResponse<ILutPropertyTypeModel[]>>(
            environment.apiUrl + '/lutTables/propertyTypeLut/selector',
            {
              params: {
                ...query,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getPropTypeLutSuccess({
                  payload: { propTypeLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getEstateTypeLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getEstateTypeLut),
      switchMap(({ payload }) => {
        let query = {};
        query = { ...payload };
        return this.http
          .get<IResponse<ILutEstateTypeModel[]>>(
            environment.apiUrl + '/lutTables/estateTypeLut/selector',
            {
              params: {
                ...query,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getEstateTypeLutSuccess({
                  payload: { estateTypeLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getRightTypeLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getRightTypeLut),
      switchMap(({ payload }) => {
        let query = {};
        query = { ...payload };
        return this.http
          .get<IResponse<ILutRightTypeModel[]>>(
            environment.apiUrl + '/lutTables/rightTypeLut/selector',
            {
              params: {
                ...query,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getRightTypeLutSuccess({
                  payload: { rightTypeLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getRightStatusLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getRightStatusLut),
      switchMap(({ payload }) => {
        let query = {};
        query = { ...payload };
        return this.http
          .get<IResponse<ILutRightStatusModel[]>>(
            environment.apiUrl + '/lutTables/rightStatusLut/selector',
            {
              params: {
                ...query,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getRightStatusLutSuccess({
                  payload: { rightStatusLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getDocTypeLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getDocTypeLut),
      switchMap(({ payload }) => {
        let query = {};
        query = { ...payload };
        return this.http
          .get<IResponse<ILutDocTypeModel[]>>(
            environment.apiUrl + '/lutTables/docTypeLut/selector',
            {
              params: {
                ...query,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getDocTypeLutSuccess({
                  payload: { docTypeLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getWayownLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getWayownLut),
      switchMap(({ payload }) => {
        let query = {};
        query = { ...payload };
        return this.http
          .get<IResponse<ILutWayownModel[]>>(
            environment.apiUrl + '/lutTables/wayownLut/selector',
            {
              params: {
                ...query,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getWayownLutSuccess({
                  payload: { wayownLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getIssuerTypeLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getIssuerTypeLut),
      switchMap(({ payload }) => {
        let query = {};
        query = { ...payload };
        return this.http
          .get<IResponse<ILutIssuerTypeModel[]>>(
            environment.apiUrl + '/lutTables/issuerTypeLut/selector',
            {
              params: {
                ...query,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getIssuerTypeLutSuccess({
                  payload: { issuerTypeLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getIssuerHqLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getIssuerHqLut),
      switchMap(({ payload }) => {
        let query = {};
        query = { ...payload };
        return this.http
          .get<IResponse<ILutIssuerHqModel[]>>(
            environment.apiUrl + '/lutTables/issuerHqLut/selector',
            {
              params: {
                ...query,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getIssuerHqLutSuccess({
                  payload: { issuerHqLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );
  getTopoTypeLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getTopoTypeLut),
      switchMap(({ payload }) => {
        let query = {};
        query = { ...payload };
        return this.http
          .get<IResponse<ILutTopoTypeModel[]>>(
            environment.apiUrl + '/lutTables/topoTypeLut/selector',
            {
              params: {
                ...query,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getTopoTypeLutSuccess({
                  payload: { topoTypeLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getBenTypeLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getBenTypeLut),
      switchMap(({ payload }) => {
        let query = {};
        query = { ...payload };
        return this.http
          .get<IResponse<ILutBenTypeModel[]>>(
            environment.apiUrl + '/lutTables/benTypeLut/selector',
            {
              params: {
                ...query,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getBenTypeLutSuccess({
                  payload: { benTypeLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getMisthosiLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getMisthosiLut),
      switchMap(({ payload }) => {
        return this.http
          .get<any>(environment.apiUrl + '/lutTables/misthosiLut/selector', {
            params: {
              searchText: payload.searchText,
            },
          })
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getMisthosiLutSuccess({
                  payload: { misthosi: response },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getEnergyClassLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getEnergyClassLut),
      switchMap(({ payload }) => {
        return this.http
          .get<IResponse<ILutEnergyClassModel[]>>(
            environment.apiUrl + '/lutTables/energyClass/selector',
            {
              params: {
                searchText: payload.searchText,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getEnergyClassLutSuccess({
                  payload: { energyClassLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getPropEidosLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getPropEidosLut),
      switchMap(({ payload }) => {
        return this.http
          .get<IResponse<ILutPropEidosModel[]>>(
            environment.apiUrl + '/lutTables/propEidos/selector',
            {
              params: {
                searchText: payload.searchText,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getPropEidosLutSuccess({
                  payload: { propEidosLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getLutUtilitiesText$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getLutUtilityTypeText),
      switchMap(({ payload }) => {
        return this.http
          .get<IResponse<ILutUtilityTypeModel[]>>(
            environment.apiUrl + '/lutTables/utilityType/selector',
            {
              params: {
                searchText: payload.searchText,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getLutUtilityTypeSuccess({
                  payload: { lutUtilityType: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getUseDurationUnitsLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getUseDurationUnitsLut),
      switchMap(({ payload }) => {
        return this.http
          .get<IResponse<ILutUseDurationUnitsModel[]>>(
            environment.apiUrl + '/lutTables/durationUnits/selector',
            {
              params: {
                searchText: payload.searchText,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getUseDurationUnitsLutSuccess({
                  payload: { useDurationUnitsLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  addIssuerHqLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.addIssuerHqLut),
      switchMap(({ payload }) => {
        this.store.dispatch(
          DocActions.docLoading({ payload: { loading: true } })
        );
        return this.http
          .post<any>(environment.apiUrl + '/lutTables/issuerHqLut/add', {
            payload,
          })
          .pipe(
            mergeMap((response) => {
              this.store.dispatch(
                DocActions.docLoading({ payload: { loading: false } })
              );
              return of(LutTablesActions.addIssuerHqLutSuccess());
            }),
            catchError((error) => {
              this.store.dispatch(
                DocActions.docLoading({ payload: { loading: false } })
              );
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  editIssuerHqLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.editIssuerHqLut),
      switchMap(({ payload }) => {
        this.store.dispatch(
          DocActions.docLoading({ payload: { loading: true } })
        );
        return this.http
          .put<any>(
            environment.apiUrl +
            '/lutTables/issuerHqLut/edit/' +
            payload.issuer_hq_code,
            {
              payload,
            }
          )
          .pipe(
            mergeMap((response) => {
              this.store.dispatch(
                DocActions.docLoading({ payload: { loading: false } })
              );
              return of(LutTablesActions.editIssuerHqLutSuccess());
            }),
            catchError((error) => {
              this.store.dispatch(
                DocActions.docLoading({ payload: { loading: false } })
              );

              if (
                environment.optimisticLockErrors.includes(error.error?.name)
              ) {
                return of(
                  LutTablesActions.IssuerHQOptimisticLockError({
                    payload: {
                      optimisticLockError: this.errorService.mapError(error),
                    },
                  })
                );
              } else {
                return of(
                  LutTablesActions.lutTablesFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
              }
            })
          );
      })
    )
  );

  deleteIssuerHqLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.deleteIssuerHqLut),
      switchMap(({ payload }) => {
        this.store.dispatch(
          DocActions.docLoading({ payload: { loading: true } })
        );
        return this.http
          .delete<any>(
            environment.apiUrl +
            '/lutTables/issuerHqLut/delete/' +
            payload.issuer_hq_code
          )
          .pipe(
            mergeMap((response) => {
              this.store.dispatch(
                DocActions.docLoading({ payload: { loading: false } })
              );
              return of(LutTablesActions.deleteIssuerHqLutSuccess());
            }),
            catchError((error) => {
              this.store.dispatch(
                DocActions.docLoading({ payload: { loading: false } })
              );
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );


  addPropEidosLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.addPropEidosLut),
      switchMap(({ payload }) => {
        this.store.dispatch(
          BuildingActions.buildingLoading({ payload: { loading: true } })
        );
        return this.http
          .post<any>(environment.apiUrl + '/lutTables/propEidos/add', {
            payload,
          })
          .pipe(
            mergeMap((response) => {
              this.store.dispatch(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
              return of(LutTablesActions.addPropEidosLutSuccess());
            }),
            catchError((error) => {
              this.store.dispatch(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  editPropEidosLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.editPropEidosLut),
      switchMap(({ payload }) => {
        this.store.dispatch(
          BuildingActions.buildingLoading({ payload: { loading: true } })
        );
        return this.http
          .put<any>(
            environment.apiUrl +
            '/lutTables/propEidos/edit/' +
            payload.id,
            {
              payload,
            }
          )
          .pipe(
            mergeMap((response) => {
              this.store.dispatch(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
              return of(LutTablesActions.editPropEidosLutSuccess());
            }),
            catchError((error) => {
              this.store.dispatch(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );

              if (
                environment.optimisticLockErrors.includes(error.error?.name)
              ) {
                return of(
                  LutTablesActions.propEidosOptimisticLockError({
                    payload: {
                      optimisticLockError: this.errorService.mapError(error),
                    },
                  })
                );
              } else {
                return of(
                  LutTablesActions.lutTablesFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
              }
            })
          );
      })
    )
  );

  deletePropEidosLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.deletePropEidosLut),
      switchMap(({ payload }) => {
        this.store.dispatch(
          BuildingActions.buildingLoading({ payload: { loading: true } })
        );
        return this.http
          .delete<any>(
            environment.apiUrl +
            '/lutTables/propEidos/delete/' +
            payload.id
          )
          .pipe(
            mergeMap((response) => {
              this.store.dispatch(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
              return of(LutTablesActions.deletePropEidosLutSuccess());
            }),
            catchError((error) => {
              this.store.dispatch(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getMonthlyCostTypeLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.getMonthlyCostTypeLut),
      switchMap(({ payload }) => {
        return this.http
          .get<IResponse<ILutMonthlyCostTypeModel[]>>(
            environment.apiUrl + '/lutTables/monthlyCostType/selector',
            {
              params: {
                searchText: payload.searchText,
              },
            }
          )
          .pipe(
            mergeMap((response) => {
              return of(
                LutTablesActions.getMonthlyCostTypeLutSuccess({
                  payload: { monthlyCostTypeLut: response.data },
                })
              );
            }),
            catchError((error) => {
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  addMonthlyCostTypeLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.addMonthlyCostTypeLut),
      switchMap(({ payload }) => {
        this.store.dispatch(
          BuildingActions.buildingLoading({ payload: { loading: true } })
        );
        return this.http
          .post<any>(environment.apiUrl + '/lutTables/monthlyCostType/add', {
            payload,
          })
          .pipe(
            mergeMap((response) => {
              this.store.dispatch(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
              return of(LutTablesActions.addMonthlyCostTypeLutSuccess());
            }),
            catchError((error) => {
              this.store.dispatch(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  editMonthlyCostTypeLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.editMonthlyCostTypeLut),
      switchMap(({ payload }) => {
        this.store.dispatch(
          BuildingActions.buildingLoading({ payload: { loading: true } })
        );
        return this.http
          .put<any>(
            environment.apiUrl +
            '/lutTables/monthlyCostType/edit/' +
            payload.id,
            {
              payload,
            }
          )
          .pipe(
            mergeMap((response) => {
              this.store.dispatch(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
              return of(LutTablesActions.editMonthlyCostTypeLutSuccess());
            }),
            catchError((error) => {
              this.store.dispatch(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );

              if (
                environment.optimisticLockErrors.includes(error.error?.name)
              ) {
                return of(
                  LutTablesActions.monthlyCostTypeOptimisticLockError({
                    payload: {
                      optimisticLockError: this.errorService.mapError(error),
                    },
                  })
                );
              } else {
                return of(
                  LutTablesActions.lutTablesFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
              }
            })
          );
      })
    )
  );

  deleteMonthlyCostTypeLut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.deleteMonthlyCostTypeLut),
      switchMap(({ payload }) => {
        this.store.dispatch(
          BuildingActions.buildingLoading({ payload: { loading: true } })
        );
        return this.http
          .delete<any>(
            environment.apiUrl +
            '/lutTables/monthlyCostType/delete/' +
            payload.id
          )
          .pipe(
            mergeMap((response) => {
              this.store.dispatch(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
              return of(LutTablesActions.deleteMonthlyCostTypeLutSuccess());
            }),
            catchError((error) => {
              this.store.dispatch(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
              return of(
                LutTablesActions.lutTablesFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );


  updateLandUseGpsStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LutTablesActions.updateLandUseGpsStatus),
      switchMap(({ id, inactive }) => {
        return this.http
          .put<IResponse<any>>(environment.apiUrl + '/updateLandUseGpsStatus', { id, inactive })
          .pipe(
            map(response => LutTablesActions.updateLandUseGpsStatusSuccess({ landUseGpsItem: response.data })),
            catchError(error => of(LutTablesActions.updateLandUseGpsStatusFail({ error: this.errorService.mapError(error) })))
          );
      })
    )
  );
}
